// https://boost.allnetru.pro/api/dev/dicts/routes?prefix=api/partner/meetings
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import { MEETINGS_TAGS } from '../../tags';
import type {
  ChangeMeetingStatusRequest,
  CreateMeetingOfferRequest,
  MeetingBlockResponse,
  MeetingCalendarParams,
  MeetingCalendarResponse,
  MeetingResponse,
  MeetingSettingsResponse,
  MeetingSlotsParams,
  MeetingSlotsResponse,
  MeetingsParams,
  MeetingsResponse,
  MeetingSummaryResponse,
  MeetingTeamResponse,
  MeetingWindowResponse,
  ShortlistsParams,
  ShortlistsResponse,
  StoreMeetingBlockRequest,
  StoreMeetingWindowRequest,
  UpdateMeetingBlockRequest,
  UpdateMeetingSettingsRequest,
  UpdateMeetingWindowRequest,
  UpsertTeamMemberRequest,
} from './models';

const partner_meetings = MEETINGS_TAGS.PARTNER_MEETINGS;

/** The lists a meeting change touches — a booking moves a card from Requests to Meetings and off the shortlist. */
const MEETING_TAGS = [
  { type: partner_meetings, id: 'LIST' },
  { type: partner_meetings, id: 'SHORTLISTS' },
  { type: partner_meetings, id: 'SUMMARY' },
  { type: partner_meetings, id: 'CALENDAR' },
  { type: partner_meetings, id: 'SLOTS' },
] as const;

/** The schedule a window, a block or the rules touch. */
const SCHEDULE_TAGS = [
  { type: partner_meetings, id: 'CALENDAR' },
  { type: partner_meetings, id: 'SLOTS' },
  { type: partner_meetings, id: 'TEAM' },
] as const;

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

/**
 * `filters[events][]=1&filters[statuses][]=2` — the shape the FormRequests
 * read. RTK's default serializer flattens a nested object to "[object Object]".
 */
const withFilters = (url: string, params?: MeetingsParams | ShortlistsParams | void): string => {
  const search = new URLSearchParams();
  if (params) {
    Object.entries(params.filters ?? {}).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => search.append(`filters[${key}][]`, String(item)));
      } else if (value !== undefined && value !== null && value !== '') {
        search.append(`filters[${key}]`, String(value));
      }
    });
    if (params.per_page) search.append('per_page', String(params.per_page));
    if (params.page) search.append('page', String(params.page));
  }
  const query = search.toString();
  return query ? `${url}?${query}` : url;
};

export const partnerMeetingsApi = createApi({
  reducerPath: partner_meetings,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partner_meetings],
  endpoints: (build) => ({
    getMeetings: build.query<MeetingsResponse, MeetingsParams | void>({
      query: (params) => ({
        url: withFilters('/partner/meetings', params),
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_meetings, id: 'LIST' }],
    }),
    getShortlists: build.query<ShortlistsResponse, ShortlistsParams | void>({
      query: (params) => ({
        url: withFilters('/partner/meetings/shortlists', params),
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_meetings, id: 'SHORTLISTS' }],
    }),
    getMeetingSummary: build.query<MeetingSummaryResponse, ShortlistsParams | void>({
      query: (params) => ({
        url: withFilters('/partner/meetings/summary', params),
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_meetings, id: 'SUMMARY' }],
    }),
    changeMeetingStatus: build.mutation<MeetingResponse, ChangeMeetingStatusRequest>({
      query: ({ meetingId, status, ...body }) => ({
        url: `/partner/meetings/${meetingId}/status/${status}`,
        method: 'PATCH',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: [...MEETING_TAGS],
    }),
    createMeetingOffer: build.mutation<MeetingResponse, CreateMeetingOfferRequest>({
      query: (body) => ({
        url: '/partner/meetings/offers',
        method: 'POST',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: [...MEETING_TAGS],
    }),

    // ── the schedule ───────────────────────────────────────────────────────
    getMeetingCalendar: build.query<MeetingCalendarResponse, MeetingCalendarParams>({
      query: (params) => ({
        url: '/partner/meetings/calendar',
        params,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_meetings, id: 'CALENDAR' }],
    }),
    getMeetingSlots: build.query<MeetingSlotsResponse, MeetingSlotsParams>({
      query: (params) => ({
        url: '/partner/meetings/slots',
        params,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_meetings, id: 'SLOTS' }],
    }),
    getMeetingSettings: build.query<MeetingSettingsResponse, { event_id: number }>({
      query: (params) => ({
        url: '/partner/meetings/settings',
        params,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_meetings, id: 'CALENDAR' }],
    }),
    updateMeetingSettings: build.mutation<MeetingSettingsResponse, UpdateMeetingSettingsRequest>({
      query: (body) => ({
        url: '/partner/meetings/settings',
        method: 'PATCH',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: [...SCHEDULE_TAGS],
    }),
    storeMeetingWindow: build.mutation<MeetingWindowResponse, StoreMeetingWindowRequest>({
      query: (body) => ({
        url: '/partner/meetings/windows',
        method: 'POST',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: [...SCHEDULE_TAGS],
    }),
    updateMeetingWindow: build.mutation<MeetingWindowResponse, UpdateMeetingWindowRequest>({
      query: ({ windowId, ...body }) => ({
        url: `/partner/meetings/windows/${windowId}`,
        method: 'PATCH',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: [...SCHEDULE_TAGS],
    }),
    deleteMeetingWindow: build.mutation<void, { windowId: number }>({
      query: ({ windowId }) => ({
        url: `/partner/meetings/windows/${windowId}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [...SCHEDULE_TAGS],
    }),
    storeMeetingBlock: build.mutation<MeetingBlockResponse, StoreMeetingBlockRequest>({
      query: (body) => ({
        url: '/partner/meetings/blocks',
        method: 'POST',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: [...SCHEDULE_TAGS],
    }),
    updateMeetingBlock: build.mutation<MeetingBlockResponse, UpdateMeetingBlockRequest>({
      query: ({ blockId, ...body }) => ({
        url: `/partner/meetings/blocks/${blockId}`,
        method: 'PATCH',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: [...SCHEDULE_TAGS],
    }),
    deleteMeetingBlock: build.mutation<void, { blockId: number }>({
      query: ({ blockId }) => ({
        url: `/partner/meetings/blocks/${blockId}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [...SCHEDULE_TAGS],
    }),
    getMeetingTeam: build.query<MeetingTeamResponse, { event_id: number }>({
      query: (params) => ({
        url: '/partner/meetings/team',
        params,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partner_meetings, id: 'TEAM' }],
    }),
    upsertMeetingTeamMember: build.mutation<MeetingTeamResponse, UpsertTeamMemberRequest>({
      query: ({ userId, ...body }) => ({
        url: `/partner/meetings/team/${userId}`,
        method: 'PUT',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: [...SCHEDULE_TAGS],
    }),
  }),
});

export const {
  useGetMeetingsQuery,
  useGetShortlistsQuery,
  useGetMeetingSummaryQuery,
  useChangeMeetingStatusMutation,
  useCreateMeetingOfferMutation,
  useGetMeetingCalendarQuery,
  useGetMeetingSlotsQuery,
  useGetMeetingSettingsQuery,
  useUpdateMeetingSettingsMutation,
  useStoreMeetingWindowMutation,
  useUpdateMeetingWindowMutation,
  useDeleteMeetingWindowMutation,
  useStoreMeetingBlockMutation,
  useUpdateMeetingBlockMutation,
  useDeleteMeetingBlockMutation,
  useGetMeetingTeamQuery,
  useUpsertMeetingTeamMemberMutation,
} = partnerMeetingsApi;
